import React, { Fragment, useEffect, useMemo, useState } from "react"

import { Trans, useTranslation } from "react-i18next"
import { useInView } from "react-intersection-observer"
import { useInfiniteQuery, useMutation, useQuery } from "react-query"
import { useSelector } from "react-redux"
import { useParams, useSearchParams } from "react-router-dom"

import MoreVertIcon from "@mui/icons-material/MoreVert"
import { CircularProgress, Grid, IconButton, Skeleton } from "@mui/material"
import {
  Button,
  DateRangePicker,
  Menu,
  MenuItem,
  NotificationUtils,
  Tab,
  Tabs,
  Tag,
  Typography,
} from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import moment from "moment"

import { InfoContainer } from "../components/core/InfoContainer"
import { KonanEmptyState } from "../components/utility/KonanEmptyState"
import { ReportContainer } from "../features/Insights"
import { getTheme } from "../hooks/UseTheme"
import { useDateQuery } from "../hooks/useDateQuery"
import { KonanAPI } from "../services/KonanAPI"
import { queryClient } from "../store/QueryClientStore"
import type { RootState } from "../store/ReduxStore"
import type { baseErrorType } from "../types/custom/projects"
import type { DeploymentPredictionsSummary } from "../types/generated/api/DeploymentPredictionsSummary"
import type { PaginatedReportListCreateList } from "../types/generated/api/PaginatedReportListCreateList"
import type { Report } from "../types/generated/api/Report"
import { ReportListCreate } from "../types/generated/api/ReportListCreate"
import { ReportListCreateRequest } from "../types/generated/api/ReportListCreateRequest"
import { extractPageFromBackEndPaginationLink, getEnvVar } from "../utils/genericHelpers"

import styles from "./Simulations.module.scss"

/**
 * Enum mapping dashboard statuses to tag variants
 * Maps each possible report status to a corresponding UI tag variant
 */
enum DashboardsTagMap {
  "PENDING" = "blue",
  "PREPARATION" = "yellow",
  "RUNNING" = "yellow",
  "FAILED" = "red",
  "CANCELLED" = "red",
  "SUCCEEDED" = "green",
  "PARTIALLY_SUCCEEDED" = "orange",
}

/**
 * Renders placeholder skeleton loaders for report items during loading states
 * Displays three skeleton items in a vertical layout to indicate loading progress
 * @returns {React.ReactElement} Grid items containing skeleton loading animations
 */
const ReportItemLoaders = (): React.ReactElement => (
  <Fragment>
    {[1, 2, 3].map((item) => {
      return (
        <Grid item xs={12} key={item}>
          <Skeleton animation="wave" width="75%" height={34} />
        </Grid>
      )
    })}
  </Fragment>
)

/**
 * Converts start and end dates to a formatted string
 * @param {string} startDate - The start date
 * @param {string} endDate - The end date
 * @returns {string} Formatted date range string
 */
const convertDatesToString = (startDate: string, endDate: string): string => {
  return `${moment(startDate).format("MMM DD YYYY")} -> ${moment(endDate).format("MMM DD YYYY")}`
}

/**
 * Insights Component
 *
 * This component displays an overview of Insights and reports.
 * Features:
 * - Date range selection for report generation
 * - Infinite-scrolling list of recent reports
 * - Report status visualization
 * - Report management (create, delete, cancel)
 * - Sidebar toggle for recent reports
 *
 * State Management:
 * - Handles selected report state
 * - Manages date range selection
 * - Controls sidebar visibility
 * - Handles report actions through mutations
 *
 * @returns {React.ReactElement} The rendered Insights component
 */
export function Insights(): React.ReactElement {
  // Extract project ID from URL parameters and manage search params
  const { id: projectId } = useParams<{ id: string }>()
  const [searchParams, setSearchParams] = useSearchParams()

  const theme = getTheme()

  // Get report ID from URL query parameters if it exists
  const queryParamReport = searchParams.get("report")

  const currentProject = useSelector((state: RootState) => state.project.currentProject)

  const { t } = useTranslation("insights", {
    keyPrefix: "insightsReport",
  })

  // Configure intersection observer for infinite scroll functionality
  // Only triggers next page fetch when last item is in view and more data is available
  const { ref: lastReportRef } = useInView({
    onChange: (inView: boolean) => {
      // Fetch next page only when: element is in view AND has more pages AND not currently fetching
      inView && hasNextPage && !isFetchingNextPage && fetchNextPage()
    },
  })

  // Initialize date range state with default of last 29 days
  const [startDate, setStartDate, endDate, setEndDate] = useDateQuery(29)

  const insightsMinimumEntries = Number(getEnvVar("INSIGHTS_MINIMUM_ENTRIES"))

  // UI State Management
  const [selectedReport, setSelectedReport] = useState<{
    uuid: string
    status: ReportListCreate.status
    name: string
  } | null>(null)

  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false)

  const [moreMenuAnchor, setMoreMenuAnchor] = useState<null | HTMLElement>(null)

  const tab = searchParams.get("tab") === "feedback" && currentProject?.type !== "generic" ? "feedback" : "predictions"

  // Fetch paginated insights reports with infinite scroll support
  const {
    isLoading: isLoadingInsights,
    data: insightsData,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  } = useInfiniteQuery<AxiosResponse<PaginatedReportListCreateList>, AxiosError>(
    ["dashboard-reports", projectId],
    ({ pageParam = 1 }) =>
      KonanAPI.fetchInsightsReports({
        project_uuid: projectId as string,
        page_size: 20,
        page: pageParam,
        type: "PREDICTION",
      }),
    {
      onSuccess: (response) => {
        // If report ID exists in URL, set it up; otherwise use first report from response
        if (queryParamReport) {
          setupReport(JSON.parse(queryParamReport))
        } else if (!selectedReport) {
          const report = response.pages[0]?.data.results?.[0]
          if (report) {
            setupReport(report)
          }
        }
      },
      // Extract next page number from backend pagination link for infinite scroll
      getNextPageParam: (lastPage) => {
        return lastPage.data?.next ? extractPageFromBackEndPaginationLink(lastPage.data?.next) : false
      },
      enabled: !!projectId,
      refetchOnMount: true,
    },
  )

  // Fetch API requests summary for selected date range
  // Only fetches if no insights data exists (empty state)
  const { isLoading: isLoadingApiRequestsSummary, data: apiRequestsSummary } = useQuery<
    AxiosResponse<DeploymentPredictionsSummary>
  >(
    ["apiRequests", projectId, startDate, endDate],
    () =>
      KonanAPI.fetchApiRequestsSummary({
        project_uuid: projectId as string,
        start_date: startDate?.toISOString() as string,
        end_date: endDate?.toISOString() as string,
      }),
    {
      enabled: !!projectId && !!startDate && !!endDate && !insightsData?.pages[0]?.data.count,
    },
  )

  // Flatten paginated insights data into a single array for easier rendering
  const adjustedInsights = useMemo(() => {
    return (
      insightsData?.pages.reduce((accumulator, page) => {
        return accumulator.concat(page.data.results as ReportListCreate[])
      }, [] as ReportListCreate[]) ?? ([] as ReportListCreate[])
    )
  }, [insightsData?.pages])

  // Mutation handlers for report management
  const createInsightMutation = useMutation<
    AxiosResponse<Report>,
    AxiosError<baseErrorType>,
    { start: moment.Moment; end: moment.Moment }
  >(
    ({ start, end }) =>
      KonanAPI.createInsightsReport({
        project_uuid: projectId as string,
        predictions_start_time: start?.toISOString(),
        // Prevent future predictions by capping end date to current time
        predictions_end_time: end?.isAfter(moment()) ? moment().toISOString() : end?.toISOString(),
        type: ReportListCreateRequest.type.PREDICTION,
      }),
    {
      onSuccess: async () => {
        NotificationUtils.toast(t("notifications.createInsight.success"), { severity: "success" })
        // Refresh reports list to include newly created report
        await queryClient.invalidateQueries(["dashboard-reports", projectId])
      },
      onError: ({ response }) => {
        NotificationUtils.toast(response?.data?.details ?? t("notifications.createInsight.error"), {
          severity: "error",
        })
      },
    },
  )

  const deleteReportMutation = useMutation<AxiosResponse, AxiosError, { project_uuid: string; report_uuid: string }>(
    KonanAPI.deleteInsightsReport,
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(["dashboard-reports", projectId])

        NotificationUtils.toast(t("notifications.deleteReport.success"), { severity: "success" })
      },
      onError: () => {
        NotificationUtils.toast(t("notifications.deleteReport.error"), { severity: "error" })
      },
    },
  )

  const cancelReportMutation = useMutation<AxiosResponse, AxiosError, { project_uuid: string; report_uuid: string }>(
    KonanAPI.cancelInsightsReport,
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(["dashboard-reports", projectId])

        NotificationUtils.toast(t("notifications.cancelReport.success"), { severity: "success" })
      },
      onError: () => {
        NotificationUtils.toast(t("notifications.cancelReport.error"), { severity: "error" })
      },
    },
  )

  /**
   * Sets the currently selected report in the URL query parameters
   * @param {Object} report - The report to set in query params
   * @param {string} report.uuid - Unique identifier of the report
   * @param {ReportListCreate.status} report.status - Current status of the report
   * @param {string} report.name - Display name of the report
   */
  const setReportInQueryParams = (report: { uuid: string; status: ReportListCreate.status; name: string }): void => {
    const stringifiedParams = JSON.stringify(report)

    setSearchParams((prev) => {
      if (!["{}", null].includes(stringifiedParams)) {
        prev.set("report", stringifiedParams)
      } else {
        prev.delete("report")
      }

      return prev
    })
  }

  /**
   * Sets up a report by updating the selected report state and date range
   * @param {Object} report - The report to set up
   * @param {string} [report.predictions_start_time] - Start time of the report's predictions
   * @param {string} [report.predictions_end_time] - End time of the report's predictions
   * @param {string} report.uuid - Unique identifier of the report
   * @param {ReportListCreate.status} report.status - Current status of the report
   * @param {string} [report.name] - Optional display name of the report
   */
  const setupReport = (report: {
    predictions_start_time?: string
    predictions_end_time?: string
    uuid: string
    status: ReportListCreate.status
    name?: string
  }): void => {
    const reportName =
      report.name ||
      convertDatesToString(report.predictions_start_time as string, report.predictions_end_time as string)

    setSelectedReport({
      name: reportName,
      uuid: report.uuid,
      status: report.status,
    })

    // Update date range to match selected report
    const startMoment = report.name
      ? moment(report.name.split(" -> ")[0], "MMM DD YYYY")
      : moment(report.predictions_start_time)

    const endMoment = report.name
      ? moment(report.name.split(" -> ")[1], "MMM DD YYYY")
      : moment(report.predictions_end_time)

    setStartDate(startMoment)
    setEndDate(endMoment)

    // Only set query params for reports from the list
    if (!report.name) {
      setReportInQueryParams({
        uuid: report.uuid,
        status: report.status,
        name: reportName,
      })
    }
  }

  const handleTabChange = (value: "predictions" | "feedback"): void => {
    setSearchParams((prev) => {
      prev.set("tab", value)
      return prev
    })
  }

  // Clean up date range on component unmount
  useEffect(() => {
    return () => {
      setStartDate(moment().subtract(29, "days"))
      setEndDate(moment())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Internal component that handles different screen states and renders appropriate content
   * Manages three main states:
   * 1. Loading state with progress indicator
   * 2. Reports list with data visualization
   * 3. Empty state with date picker and generate button
   * @returns {React.ReactElement} The appropriate content based on current loading and data state
   */
  const ScreenStateSwitch = (): React.ReactElement => {
    // Show loading state
    if (isLoadingInsights) return <InfoContainer icon={<CircularProgress />} title={t("loading")} />

    // Show reports list if data exists
    if (!isLoadingInsights && adjustedInsights.length > 0)
      return (
        <Fragment>
          {/* Main content layout */}
          <Grid item xs style={{ overflowX: "hidden", height: "100%", width: "100%" }}>
            {/* Date picker and controls section */}
            <Grid
              container
              item
              xs={12}
              p={2}
              justifyContent={"space-between"}
              className={`${styles.bottom_border} ${styles.dateRangeBar}`}
            >
              <DateRangePicker
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={setStartDate}
                onEndDateChange={(endDate: moment.Moment | null) => {
                  setEndDate(endDate)
                  endDate && createInsightMutation.mutateAsync({ start: startDate as moment.Moment, end: endDate })
                }}
                disableFuture
                key={projectId}
              />

              {/* TODO:: add periodic reports btns */}

              <Button
                variant="secondary"
                onClick={() => {
                  setIsSidebarOpen((isOpen) => !isOpen)
                }}
              >
                {isSidebarOpen ? t("sidebar.close") : t("sidebar.open")}
              </Button>
            </Grid>

            {/* Selected report header section */}
            <Grid
              item
              xs={12}
              p={2}
              pb={currentProject?.type !== "generic" ? 0 : 2}
              display={"flex"}
              justifyContent={"space-between"}
              className={`${styles.bottom_border} ${styles.tabsBar}`}
            >
              {isLoadingInsights ? (
                <Skeleton animation="wave" width="300px" height={26} />
              ) : (
                <Fragment>
                  <Grid item display={"flex"} gap={1}>
                    <Typography variant="h3-bold">{selectedReport?.name}</Typography>
                  </Grid>

                  {currentProject?.type !== "generic" && (
                    <Grid item>
                      <Tabs value={tab}>
                        <Tab
                          label={t("tabs.predictions")}
                          value="predictions"
                          selected={tab === "predictions"}
                          onClick={() => handleTabChange("predictions")}
                          size="large"
                        />
                        <Tab
                          label={t("tabs.feedback")}
                          value="feedback"
                          selected={tab === "feedback"}
                          onClick={() => handleTabChange("feedback")}
                          size="large"
                        />
                      </Tabs>
                    </Grid>
                  )}

                  <Grid item>
                    <IconButton
                      style={{ padding: "0px" }}
                      onClick={(event: React.MouseEvent<HTMLButtonElement>) => setMoreMenuAnchor(event.currentTarget)}
                      aria-label="settings"
                      size="small"
                    >
                      <MoreVertIcon sx={{ color: "var(--base-text-2)" }} />
                    </IconButton>

                    <Menu
                      anchorEl={moreMenuAnchor}
                      open={Boolean(moreMenuAnchor)}
                      onClose={() => setMoreMenuAnchor(null)}
                      menuMaxContent
                    >
                      <MenuItem
                        disabled={deleteReportMutation.isLoading || cancelReportMutation.isLoading}
                        onClick={() => {
                          ;[
                            ReportListCreate.status.SUCCEEDED,
                            ReportListCreate.status.PARTIALLY_SUCCEEDED,
                            ReportListCreate.status.CANCELLED,
                            ReportListCreate.status.FAILED,
                          ].includes(selectedReport?.status as ReportListCreate.status)
                            ? deleteReportMutation.mutateAsync({
                                project_uuid: projectId as string,
                                report_uuid: selectedReport?.uuid as string,
                              })
                            : cancelReportMutation.mutateAsync({
                                project_uuid: projectId as string,
                                report_uuid: selectedReport?.uuid as string,
                              })

                          setMoreMenuAnchor(null)
                        }}
                        key={"delete-cancel-btn"}
                      >
                        <Typography variant="label-bold" color="negative" variantColor={2}>
                          {[
                            ReportListCreate.status.SUCCEEDED,
                            ReportListCreate.status.PARTIALLY_SUCCEEDED,
                            ReportListCreate.status.CANCELLED,
                            ReportListCreate.status.FAILED,
                          ].includes(selectedReport?.status as ReportListCreate.status)
                            ? t("menuActions.deleteReport")
                            : t("menuActions.cancelReport")}
                        </Typography>
                      </MenuItem>
                    </Menu>
                  </Grid>
                </Fragment>
              )}
            </Grid>

            {/* Report content section */}
            {selectedReport && (
              <Grid item xs={12} p={2} className={styles.insights_content_container}>
                <ReportContainer uuid={selectedReport.uuid} />
              </Grid>
            )}
          </Grid>

          {/* Sidebar for recent reports list */}
          {isSidebarOpen && (
            <Grid container item className={styles.left_sideBar} p={2} pb={0}>
              <Grid item xs={12}>
                <Typography variant={"a"} gutterBottom>
                  {t("sidebar.title")}
                </Typography>
              </Grid>

              <Grid
                container
                item
                xs={12}
                spacing={1}
                pb={2}
                display={"block"}
                className={styles.sideBar_content_insights}
              >
                {isLoadingInsights ? (
                  <ReportItemLoaders />
                ) : (
                  <Fragment>
                    {adjustedInsights.map((report: ReportListCreate, index: number) => (
                      <Grid item xs={12} key={report.uuid}>
                        <MenuItem
                          onClick={() => {
                            setSelectedReport({
                              uuid: report.uuid,
                              status: report.status,
                              name: convertDatesToString(
                                report.predictions_start_time as string,
                                report.predictions_end_time as string,
                              ),
                            })

                            setStartDate(moment(report.predictions_start_time))
                            setEndDate(moment(report.predictions_start_time))
                            setReportInQueryParams({
                              uuid: report.uuid,
                              status: report.status,
                              name: convertDatesToString(
                                report.predictions_start_time as string,
                                report.predictions_end_time as string,
                              ),
                            })
                          }}
                          isSelected={selectedReport?.uuid === report.uuid}
                          // adding `hasNextPage` to prevent screen re-render
                          ref={hasNextPage && index === adjustedInsights.length - 1 ? lastReportRef : undefined}
                        >
                          <Typography variant={"p"}>
                            {convertDatesToString(
                              report.predictions_start_time as string,
                              report.predictions_end_time as string,
                            )}
                          </Typography>

                          <Tag
                            size="small"
                            color={DashboardsTagMap[report?.status as keyof typeof DashboardsTagMap]}
                            variant="filled"
                          >
                            {t(`status.${report.status.toLowerCase()}`, {
                              defaultValue:
                                report.status === ReportListCreate.status.PARTIALLY_SUCCEEDED
                                  ? "Partial Success"
                                  : report.status.charAt(0).toUpperCase() + report.status.slice(1)?.toLocaleLowerCase(),
                            })}
                          </Tag>
                        </MenuItem>
                      </Grid>
                    ))}

                    {isFetchingNextPage && <ReportItemLoaders />}
                  </Fragment>
                )}
              </Grid>
            </Grid>
          )}
        </Fragment>
      )

    // Show empty state when no reports exist
    return (
      <Grid container alignContent={"center"}>
        <Grid item xs={12}>
          <KonanEmptyState title={t("emptyState.title")} subTitle={t("emptyState.subTitle")} />
        </Grid>

        <Grid container item xs={12} display={"flex"} justifyContent={"center"} gap={0}>
          <Grid item mr={1}>
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
              disableFuture
              key={projectId}
            />
          </Grid>

          <Grid item>
            <Button
              variant="primary"
              size="regular"
              onClick={() =>
                createInsightMutation.mutateAsync({
                  start: startDate as moment.Moment,
                  end: endDate as moment.Moment,
                })
              }
              isLoading={createInsightMutation.isLoading}
              disabled={
                createInsightMutation.isLoading ||
                isLoadingApiRequestsSummary ||
                (apiRequestsSummary?.data.total ?? 0) < insightsMinimumEntries
              }
            >
              {t("emptyState.button")}
            </Button>
          </Grid>

          <Grid item xs={12} gap={1} display={"flex"} justifyContent={"center"} mt={1}>
            <Typography variant="label-regular" variantColor={2} display="inline">
              (
              <strong
                style={{
                  color:
                    (apiRequestsSummary?.data?.total ?? 0) < insightsMinimumEntries
                      ? theme.palette.red.border[1]
                      : theme.palette.green.border[1],
                }}
              >
                {isLoadingApiRequestsSummary ? (
                  <Skeleton
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                    }}
                    animation="wave"
                    width="20px"
                    height={20}
                  />
                ) : (
                  (apiRequestsSummary?.data.total ?? 0)
                )}
              </strong>
              ) {t("emptyState.description.dataPoints")}
              {(apiRequestsSummary?.data.total ?? 0) < insightsMinimumEntries && (
                <Fragment>
                  <br />
                  <Trans>
                    {t("emptyState.description.minimumRequired", { minimumEntries: insightsMinimumEntries })}
                  </Trans>
                </Fragment>
              )}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    )
  }

  // Main component render
  return (
    <Grid container className={styles.root}>
      {ScreenStateSwitch()}
    </Grid>
  )
}
